package com.neurobs.ccab

import com.neurobs.ccab.casr.{AccountSubjectTestData, AccountSubjectTestTranscriptView, CasrReprocessor, ReviewedTestTranscriptCreateData, ReviewedTestTranscriptUpdateData, TRTSearchResults}
import com.neurobs.ccab.db.TestRunManager.TestRunEvent
import com.neurobs.util.JsonPrinter.jsonPrint
import com.neurobs.ccab.db._
import com.neurobs.ccab.scoring.ConfigData
import com.neurobs.http._
import com.neurobs.ccab.scoring.ConfigData._

import scala.concurrent.ExecutionContext
import io.circe.generic.auto._
import io.circe.{Decoder, Encoder}

object CCABServer {
	implicit val sexDecoder = Decoder.decodeEnumeration(Sex)
	implicit val sexEncoder = Encoder.encodeEnumeration(Sex)
	implicit val keyStorageTypeDecoder = Decoder.decodeEnumeration(KeyStorageType)
	implicit val keyStorageTypeEncoder = Encoder.encodeEnumeration(KeyStorageType)
	implicit val locationTypeDecoder = Decoder.decodeEnumeration(LocationType)
	implicit val locationTypeEncoder = Encoder.encodeEnumeration(LocationType)
	implicit val identityGroupDecoder: Decoder[IdentityGroup.Value] = Decoder.decodeEnumeration(IdentityGroup)
	implicit val identityGroupEncoder: Encoder[IdentityGroup.Value] = Encoder.encodeEnumeration(IdentityGroup)

	val liveServer = "https://ccab.neurobs.com/"
	val devServer = "https://dev.ccab.neurobs.com/"
	val localServer = "http://127.0.0.1:9000/"
	val relativeServer = "/"

	def handleResponse[T](call: RequestCall[T], callback: Option[String] => Unit)(successHandler: T => Unit)(implicit executor: ExecutionContext): RequestProcess = {
		call._2 map {
			case Left(error) => callback(Some(error.description))
			case Right(data) =>
				callback(None)
				successHandler(data)
		}
		call._1
	}
}

class CCABServer(urlPrefix: String, session: Session)(implicit executor: ExecutionContext, urlEncoder: URLUtils) {
	import CCABServer._
	import DatabaseJson._
	import SessionConfig._

	private val service = new HTTPService(urlPrefix, session)
	import service._

	def login(info: LoginData): RequestCall[Option[ValidUserData]] = {
		objectCall[Option[ValidUserData]](
			urlSuffix = "api/v1/login",
			t = HTTPRequestType.Post,
			body = jsonPrint(info)
		)
	}

	def loginStatus(): RequestCall[Option[ValidUserData]] = {
		objectCall[Option[ValidUserData]](
			urlSuffix = "api/v1/login_status",
			t = HTTPRequestType.Get
		)
	}

	def getSessionConfigData(): RequestCall[SessionConfig] = {
		toObjectResponse[SessionConfig](session.call(
			url = "https://dep.ccab.neurobs.com/session-config/config.json",
			t = HTTPRequestType.Get
		))
	}

	def getTestStationSyncData(computerId: String): RequestCall[TestStationSyncData] = {
		objectResultCall(
			urlSuffix = "api/v1/keys/test_station/sync",
			urlArguments = Seq(("computer_id", Some(computerId))),
			t = HTTPRequestType.Get
		)
	}

	def getAccountSubjectTestRunHistory(accountSubjectId: Long): RequestCall[Seq[TestRunEvent]] = {
		objectCall[Seq[TestRunEvent]](
			urlSuffix = s"api/v1/account_subjects/$accountSubjectId/test_run_history",
			t = HTTPRequestType.Get
		)
	}

	def getKey(keyIdentifier: String): RequestCall[String] = {
		objectResultCall[String](
			urlSuffix = s"api/v1/keys/$keyIdentifier",
			t = HTTPRequestType.Get
		)
	}

	def getUserAccount(accountId: Long, userId: Long): RequestCall[AccountUserDataRest] = {
		objectCall[AccountUserDataRest](
			urlSuffix = s"api/v1/accounts/$accountId/users/$userId",
			t = HTTPRequestType.Get
		)
	}

	def getAccount(accountId: Long): RequestCall[AccountDataRest] = {
		objectCall[AccountDataRest](
			urlSuffix = s"api/v1/accounts_v2/$accountId",
			t = HTTPRequestType.Get
		)
	}

	def getUsers(accountId: Long): RequestCall[Seq[UserDataRest]] = {
		objectCall[Seq[UserDataRest]](
			urlSuffix = s"api/v1/accounts/$accountId/users",
			t = HTTPRequestType.Get
		)
	}

	def getUserRoles(userId: Long): RequestCall[Seq[RoleData]] = {
		objectCall[Seq[RoleData]](
			urlSuffix = s"api/v1/users/$userId/roles",
			t = HTTPRequestType.Get
		)
	}

	def getAccounts(): RequestCall[Seq[AccountDataRest]] = {
		objectCall[Seq[AccountDataRest]](
			urlSuffix = "api/v1/accounts",
			t = HTTPRequestType.Get
		)
	}

	def createAccount(data: CreateAccountData): RequestCall[Unit] = {
		noResultCall(
			urlSuffix = "api/v1/accounts",
			body = jsonPrint(data),
			t = HTTPRequestType.Post
		)
	}

	def checkDataPassword(accountId: Long, dataPassword: String): RequestCall[Unit] = {
		generalResultCall(
			urlSuffix = s"api/v1/accounts/$accountId/data_password/check",
			urlArguments = Seq(("data_password", Some(dataPassword))),
			t = HTTPRequestType.Get
		)
	}

	def dataPasswordIsRequired(accountId: Long): RequestCall[Boolean] = {
		objectCall[Boolean](
			urlSuffix = s"api/v1/accounts/$accountId/data_password/req",
			t = HTTPRequestType.Get
		)
	}

	def getSettings(): RequestCall[Seq[SettingData]] = {
		objectCall[Seq[SettingData]](
			urlSuffix = "api/v1/settings",
			t = HTTPRequestType.Get
		)
	}

	def getSettingValues(settingId: Option[Long], userId: Option[Long], accountId: Option[Long]): RequestCall[Seq[SettingValueData]] = {
		objectCall[Seq[SettingValueData]](
			urlSuffix = "api/v1/setting_values",
			urlArguments = Seq(("setting_id", settingId),
				("user_id", userId),
				("account_id", accountId)),
			t = HTTPRequestType.Get
		)
	}

	def setSettingValue(data: SettingValueCreateData): RequestCall[Unit] = {
		generalResultCall(
			urlSuffix = "api/v1/setting_values",
			body = jsonPrint(data),
			t = HTTPRequestType.Post
		)
	}

	def createUser(accountId: Long, data: CreateUserData): RequestCall[Unit] = {
		noResultCall(
			urlSuffix = s"api/v1/accounts/$accountId/users",
			body = jsonPrint(data),
			t = HTTPRequestType.Post
		)
	}

	def migrateUser(accountId: Long, userId: Long, data: CreateUserData): RequestCall[Unit] = {
		noResultCall(
			urlSuffix = s"api/v1/accounts/$accountId/users/$userId/migrate",
			body = jsonPrint(data),
			t = HTTPRequestType.Post
		)
	}

	def resetUserPassword(userId: Long, data: ResetUserPasswordData): RequestCall[Unit] = {
		generalResultCall(
			urlSuffix = s"api/v1/users/$userId/password",
			body = jsonPrint(data),
			t = HTTPRequestType.Put
		)
	}

	def updateUser(accountId: Long, userId: Long, data: UpdateUserData): RequestCall[Unit] = {
		noResultCall(
			urlSuffix = s"api/v1/accounts/$accountId/users/$userId",
			body = jsonPrint(data),
			t = HTTPRequestType.Put
		)
	}

	def createTest(data: TestUpdateData): RequestCall[TestData] = {
		objectResultCall[TestData](
			urlSuffix = "api/v1/tests",
			body = jsonPrint(data),
			t = HTTPRequestType.Post
		)
	}

	def createExaminerTag(data: ExaminerTagCreateData): RequestCall[ExaminerTagData] = {
		objectResultCall[ExaminerTagData](
			urlSuffix = "api/v1/examiner_tags",
			body = jsonPrint(data),
			t = HTTPRequestType.Post
		)
	}

	def createStudyType(data: StudyTypeUpdateData): RequestCall[StudyTypeData] = {
		objectResultCall[StudyTypeData](
			urlSuffix = "api/v1/study_types",
			body = jsonPrint(data),
			t = HTTPRequestType.Post
		)
	}

	def getTests(): RequestCall[Seq[TestData]] = {
		objectCall[Seq[TestData]](
			urlSuffix = "api/v1/tests",
			t = HTTPRequestType.Get
		)
	}

	def getExaminerTags(): RequestCall[Seq[ExaminerTagData]] = {
		objectCall[Seq[ExaminerTagData]](
			urlSuffix = "api/v1/examiner_tags",
			t = HTTPRequestType.Get
		)
	}

	def getStudyTypes(): RequestCall[Seq[StudyTypeData]] = {
		objectCall[Seq[StudyTypeData]](
			urlSuffix = "api/v1/study_types",
			t = HTTPRequestType.Get
		)
	}

	def getControllerSessions(): RequestCall[Seq[ControllerSessionData]] = {
		objectCall[Seq[ControllerSessionData]](
			urlSuffix = "api/v1/controller_sessions",
			t = HTTPRequestType.Get
		)
	}

	def getControllerSessionToken(sessionId: String): RequestCall[ControllerSessionTokenData] = {
		objectCall[ControllerSessionTokenData](
			urlSuffix = s"api/v1/controller_sessions/token",
			urlArguments = Seq(("session_id", Some(sessionId))),
			t = HTTPRequestType.Get
		)
	}

	def getReleaseTypes(): RequestCall[Seq[ReleaseTypeData]] = {
		objectCall[Seq[ReleaseTypeData]](
			urlSuffix = "api/v1/release_types",
			t = HTTPRequestType.Get
		)
	}

	def deleteTest(testId: Long): RequestCall[Unit] = {
		generalResultCall(
			urlSuffix = s"api/v1/tests/$testId",
			t = HTTPRequestType.Delete
		)
	}

	def deleteStudyType(studyTypeId: Long): RequestCall[Unit] = {
		generalResultCall(
			urlSuffix = s"api/v1/study_types/$studyTypeId",
			t = HTTPRequestType.Delete
		)
	}

	def deleteRelease(releaseId: Long): RequestCall[Unit] = {
		noResultCall(
			urlSuffix = s"api/v1/releases/$releaseId",
			t = HTTPRequestType.Delete
		)
	}

	def updateTest(testId: Long, data: TestUpdateData): RequestCall[Unit] = {
		generalResultCall(
			urlSuffix = s"api/v1/tests/$testId",
			body = jsonPrint(data),
			t = HTTPRequestType.Put
		)
	}

	def updateExaminerTag(examinerTagId: Long, data: ExaminerTagUpdateData): RequestCall[Unit] = {
		generalResultCall(
			urlSuffix = s"api/v1/examiner_tags/$examinerTagId",
			body = jsonPrint(data),
			t = HTTPRequestType.Put
		)
	}

	def updateStudyType(studyTypeId: Long, data: StudyTypeUpdateData): RequestCall[Unit] = {
		generalResultCall(
			urlSuffix = s"api/v1/study_types/$studyTypeId",
			body = jsonPrint(data),
			t = HTTPRequestType.Put
		)
	}

	def updateRelease(releaseId: Long, data: ReleaseUpdateData): RequestCall[Unit] = {
		noResultCall(
			urlSuffix = s"api/v1/releases/$releaseId",
			body = jsonPrint(data),
			t = HTTPRequestType.Put
		)
	}

	def getReleases(releaseType: String, all: Boolean): RequestCall[Seq[ReleaseDataRest]] = {
		objectCall[Seq[ReleaseDataRest]](
			urlSuffix = s"api/v1/releases/$releaseType",
			urlArguments = Seq(("all", Some(all))),
			t = HTTPRequestType.Get
		)
	}

	def getLatestSoftwareRelease(): RequestCall[Option[ReleaseDataRest]] = {
		objectCall[Option[ReleaseDataRest]](
			urlSuffix = "api/v1/releases/controller_installers/latest",
			t = HTTPRequestType.Get
		)
	}

	def uploadSessionLog(subjectGuid: String, keyIdentifier: String, file: DataSource): RequestCall[Unit] = {
		generalResultCall(
			urlSuffix = "api/v1/test_sessions/log/admin",
			urlArguments = Seq(("subject_guid", Some(subjectGuid)), ("key_identifier", Some(keyIdentifier))),
			body = file,
			t = HTTPRequestType.Post
		)
	}

	def createRelease(releaseType: String, version: String, dependencyMinVersion: String, fileExtension: String, file: DataSource): RequestCall[CreateReleaseResult] = {
		objectCall[CreateReleaseResult](
			urlSuffix = s"api/v1/releases/$releaseType",
			urlArguments = Seq(("version", Some(version)), ("file_extension", Some(fileExtension)), ("dependency_min_version", Some(dependencyMinVersion))),
			body = file,
			t = HTTPRequestType.Post
		)
	}

	def createStudy(data: StudyUpdateData, accountId: Long): RequestCall[StudyData] = {
		objectResultCall[StudyData](
			urlSuffix = "api/v1/studies",
			urlArguments = Seq(("account_id", Some(accountId))),
			body = jsonPrint(data),
			t = HTTPRequestType.Post
		)
	}

	def getStudies(accountId: Long): RequestCall[Seq[StudyData]] = {
		objectCall[Seq[StudyData]](
			urlSuffix = "api/v1/studies",
			urlArguments = Seq(("account_id", Some(accountId))),
			t = HTTPRequestType.Get
		)
	}

	def getStudyViews(accountId: Long): RequestCall[Seq[StudyView]] = {
		objectResultCall[Seq[StudyView]](
			urlSuffix = "api/v1/studies/views",
			urlArguments = Seq(("account_id", Some(accountId))),
			t = HTTPRequestType.Get
		)
	}

	def updateSessions(data: UpdateSessionsData): RequestCall[Unit] = {
		generalResultCall(
			urlSuffix = "api/v1/test_sessions/update",
			body = jsonPrint(data),
			t = HTTPRequestType.Put
		)
	}

	def updateStudy(id: Long, data: StudyUpdateData): RequestCall[Unit] = {
		generalResultCall(
			urlSuffix = s"api/v1/studies/$id",
			body = jsonPrint(data),
			t = HTTPRequestType.Put
		)
	}

	def deleteStudy(id: Long, accountId: Long): RequestCall[Unit] = {
		generalResultCall(
			urlSuffix = s"api/v1/studies/$id",
			urlArguments = Seq(("account_id", Some(accountId))),
			t = HTTPRequestType.Delete
		)
	}

	def getSubjects(accountId: Long, dataPassword: String): RequestCall[Seq[AccountSubjectResponse]] = {
		objectResultCall[Seq[AccountSubjectResponse]](
			urlSuffix = s"api/v1/accounts/$accountId/subjects",
			urlArguments = Seq(("data_password", Some(dataPassword))),
			t = HTTPRequestType.Get
		)
	}

	def getTestSessions(accountId: Long): RequestCall[Seq[db.SessionData]] = {
		objectCall[Seq[db.SessionData]](
			urlSuffix = s"api/v1/accounts/$accountId/test_sessions",
			t = HTTPRequestType.Get
		)
	}

	def getTestSessionViews(search: SessionSearchData): RequestCall[SessionSearchResultData] = {
		objectResultCall[SessionSearchResultData](
			urlSuffix = "api/v1/test_sessions/search",
			body = jsonPrint(search),
			t = HTTPRequestType.Post
		)
	}

	def getSubjectSessions(data: SubjectSessionsRequestData): RequestCall[SubjectSessionsData] = {
		objectResultCall[SubjectSessionsData](
			urlSuffix = "api/v1/test_sessions/subject",
			body = jsonPrint(data),
			t = HTTPRequestType.Post
		)
	}

	def getAccountSubjectReportHistory(accountSubjectId: Long): RequestCall[ReportHistoryData] = {
		objectCall[ReportHistoryData](
			urlSuffix = s"api/v1/account_subjects/$accountSubjectId/report_history",
			t = HTTPRequestType.Get
		)
	}

	def getAccountSubjectTestSessions(accountSubjectId: Long): RequestCall[Seq[db.SessionData]] = {
		objectCall[Seq[db.SessionData]](
			urlSuffix = s"api/v1/account_subjects/$accountSubjectId/test_sessions",
			t = HTTPRequestType.Get
		)
	}

	def getTestRuns(testSessionId: Long): RequestCall[Seq[TestRunData]] = {
		objectCall[Seq[TestRunData]](
			urlSuffix = s"api/v1/test_sessions/$testSessionId/test_runs",
			t = HTTPRequestType.Get
		)
	}

	def getErrorTestRuns(data: TestRunSearchData): RequestCall[Seq[TestRunView]] = {
		objectCall[Seq[TestRunView]](
			urlSuffix = s"api/v1/test_runs/errors",
			body = jsonPrint(data),
			t = HTTPRequestType.Post
		)
	}

	def getLogfiles(testRunId: Long): RequestCall[Seq[LogfileData]] = {
		objectCall[Seq[LogfileData]](
			urlSuffix = s"api/v1/test_runs/$testRunId/logfiles",
			t = HTTPRequestType.Get
		)
	}

	def getSpeechAudioFileFile(testRunId: Long, wavFileCode: String): RequestCall[BodyData] = {
		binaryResponseCall(
			urlSuffix = s"api/v1/test_runs/$testRunId/speech_audio_file_file",
			urlArguments = Seq(("wav_code", Some(wavFileCode))),
			t = HTTPRequestType.Get
		)
	}

	def createSubject(accountId: Long, data: SubjectCreateData): RequestCall[AccountSubjectResponse] = {
		objectResultCall[AccountSubjectResponse](
			urlSuffix = s"api/v1/accounts/$accountId/subjects",
			body = jsonPrint(data),
			t = HTTPRequestType.Post
		)
	}

	def updateSubject(accountSubjectId: Long, data: SubjectUpdateData): RequestCall[Unit] = {
		generalResultCall(
			urlSuffix = s"api/v1/account_subjects/$accountSubjectId",
			body = jsonPrint(data),
			t = HTTPRequestType.Put
		)
	}

	def createTestSession(data: TestSessionCreateData): RequestCall[TestSessionResponse] = {
		objectResultCall[TestSessionResponse](
			urlSuffix = s"api/v1/test_sessions",
			body = jsonPrint(data),
			t = HTTPRequestType.Post
		)
	}

	def createTestRun(metadata: TestRunDataCreateFromMetadataData): RequestCall[Unit] = {
		generalResultCall(
			urlSuffix = "api/v1/test_runs/metadata",
			t = HTTPRequestType.Post,
			body = jsonPrint(metadata)
		)
	}

	def createTestRunFromLogfile(filename: String): RequestCall[Unit] = {
		generalResultCall(
			urlSuffix = "api/v1/test_runs/from_logfile",
			urlArguments = Seq(("filename", Some(filename))),
			t = HTTPRequestType.Get
		)
	}

	def uploadTestRun(metadata: TestRunDataCreateData, file: DataSource): RequestCall[Unit] = {
		generalResultCall(
			urlSuffix = "api/v1/test_runs",
			t = HTTPRequestType.Post,
			body = MultiPartSource(Seq(
				MultiPartPart(key = "file", data = file, filename = Some("file")),
				MultiPartPart(key = "metadata", data = jsonPrint(metadata))
			))
		)
	}

	def createError(errorCreateData: ErrorCreateData): RequestCall[ErrorData] = {
		objectCall[ErrorData](
			urlSuffix = "api/v1/errors",
			t = HTTPRequestType.Post,
			body = jsonPrint(errorCreateData)
		)
	}

	def createEventServerEvent(eventServerEventData: EventServerEventData): RequestCall[EventServerEventData] = {
		objectResultCall[EventServerEventData](
			urlSuffix = "api/v1/event_server_event",
			t = HTTPRequestType.Post,
			body = jsonPrint(eventServerEventData)
		)
	}


	def getTestTranscriptSelections(accountId: Long, studyId: Long, testId: Option[Long] = None, accountSubjectId: Option[Long] = None): RequestCall[TRTSearchResults] = {
		objectResultCall[TRTSearchResults](
			urlSuffix = "api/v1/test_transcripts/selections",
			urlArguments = Seq(
				("account_id", Some(accountId)),
				("study_id", Some(studyId)),
				("test_id", testId),
				("account_subject_id", accountSubjectId)
			),
			t = HTTPRequestType.Get
		)
	}

	def getReviewedTestTranscript(id: Long): RequestCall[ReviewedTestTranscriptData] = {
		objectResultCall[ReviewedTestTranscriptData](
			urlSuffix = s"api/v1/reviewed_test_transcripts/$id",
			t = HTTPRequestType.Get
		)
	}

	def getTestTranscript(id: Long): RequestCall[TestTranscriptData] = {
		objectResultCall[TestTranscriptData](
			urlSuffix = s"api/v1/test_transcripts/$id",
			t = HTTPRequestType.Get
		)
	}

	def getTestTranscriptEntry(id: Long): RequestCall[TestTranscriptData] = {
		objectResultCall[TestTranscriptData](
			urlSuffix = s"api/v1/test_transcripts/entries/$id",
			t = HTTPRequestType.Get
		)
	}

	def createReviewedTestTranscript(data: ReviewedTestTranscriptCreateData): RequestCall[ReviewedTestTranscriptData] = {
		objectResultCall[ReviewedTestTranscriptData](
			urlSuffix = "api/v1/reviewed_test_transcripts/",
			body = jsonPrint(data),
			t = HTTPRequestType.Post
		)
	}

	def updateReviewedTestTranscript(data: ReviewedTestTranscriptUpdateData): RequestCall[ReviewedTestTranscriptData] = {
		objectResultCall[ReviewedTestTranscriptData](
			urlSuffix = "api/v1/reviewed_test_transcripts/",
			body = jsonPrint(data),
			t = HTTPRequestType.Put
		)
	}

	def findTestRun(identifier: String): RequestCall[FindTestRunResult] = {
		objectResultCall[FindTestRunResult](
			urlSuffix = "api/v1/test_runs/find",
			urlArguments = Seq(
				("identifier", Some(identifier))
			),
			t = HTTPRequestType.Get
		)
	}

	def getAccountSubjectTranscriptsForStudy(studyId: Long, reviewed: Option[Boolean]): RequestCall[Seq[AccountSubjectTestTranscriptView]] = {
		objectResultCall[Seq[AccountSubjectTestTranscriptView]](
			urlSuffix = s"api/v1/studies/$studyId/account_subject_transcripts",
			urlArguments = Seq(
				("reviewed", reviewed)
			),
			t = HTTPRequestType.Get
		)
	}

	def getAccountSubjectTests(studyId: Long, reviewed: Option[Boolean]): RequestCall[AccountSubjectTestData] = {
		objectResultCall[AccountSubjectTestData](
			urlSuffix = s"api/v1/studies/$studyId/subject_tests",
			urlArguments = Seq(
				("reviewed", reviewed)
			),
			t = HTTPRequestType.Get
		)
	}

	def getSubjectScores(accountSubjectId: Long): RequestCall[Option[SubjectScoreReport]] = {
		objectCall[Option[SubjectScoreReport]](
			urlSuffix = s"api/v1/scoring_reports",
			urlArguments = Seq(
				("account_subject_id", Some(accountSubjectId))
			),
			t = HTTPRequestType.Get
		)
	}
}
